import {
  ActionTypeEnum,
  ResourceTypeEnum,
  RoleTypeEnum,
} from "@superblocksteam/shared";

export const OWNER_ROLE_NAME = "Owner";
export const ADMIN_ROLE_NAME = "Admin";

export const ROLE_TYPES: Array<RoleTypeEnum> = [
  RoleTypeEnum.APPLICATIONS,
  RoleTypeEnum.INTEGRATIONS,
  // RoleTypeEnum.PROFILES,
  RoleTypeEnum.SCHEDULED_JOBS,
  // RoleTypeEnum.SECRETS_STORES,
  RoleTypeEnum.WORKFLOWS,
];

/* Org level permissions */
export const VIEW_ACCESS_TOKENS = {
  resourceType: ResourceTypeEnum.ACCESS_TOKENS,
  action: ActionTypeEnum.READ,
};

export const MANAGE_ACCESS_TOKENS = {
  resourceType: ResourceTypeEnum.ACCESS_TOKENS,
  action: ActionTypeEnum.MANAGE,
};

export const MANAGE_PROFILES = {
  resourceType: ResourceTypeEnum.PROFILES,
  action: ActionTypeEnum.MANAGE,
};

export const VIEW_PROFILES = {
  resourceType: ResourceTypeEnum.PROFILES,
  action: ActionTypeEnum.MANAGE, // There is no READ permission so we still check MANAGE
  preRbacValue: true, // Pre-RBAC, all users could view profiles
};

export const VIEW_REPOSITORIES = {
  resourceType: ResourceTypeEnum.REPOSITORIES,
  action: ActionTypeEnum.READ,
  preRbacValue: true,
};

export const MANAGE_REPOSITORIES = {
  resourceType: ResourceTypeEnum.REPOSITORIES,
  action: ActionTypeEnum.MANAGE,
  preRbacValue: true,
};

export const VIEW_POLICIES = {
  resourceType: ResourceTypeEnum.POLICIES,
  action: ActionTypeEnum.VIEW,
};

export const MANAGE_POLICIES = {
  resourceType: ResourceTypeEnum.POLICIES,
  action: ActionTypeEnum.MANAGE,
};

export const VIEW_OBSERVABILITY = {
  resourceType: ResourceTypeEnum.LOGS_STREAMS,
  action: ActionTypeEnum.READ,
  preRbacValue: true,
};

export const MANAGE_OBSERVABILITY = {
  resourceType: ResourceTypeEnum.LOGS_STREAMS,
  action: ActionTypeEnum.MANAGE,
};

export const VIEW_AGENTS = {
  resourceType: ResourceTypeEnum.AGENTS,
  action: ActionTypeEnum.READ,
};

export const MANAGE_AGENTS = {
  resourceType: ResourceTypeEnum.AGENTS,
  action: ActionTypeEnum.MANAGE,
};

export const MANAGE_BILLING = {
  resourceType: ResourceTypeEnum.BILLING,
  action: ActionTypeEnum.MANAGE,
};

export const MANAGE_ORGANIZATION = {
  resourceType: ResourceTypeEnum.ORGANIZATION,
  action: ActionTypeEnum.MANAGE,
  preRbacValue: true,
};

// ENG-3555: gate for the Databases page. Carved out of `org.manage`
// (Owner-only) onto `app_databases.manage` so org Admins can access
// the App databases admin surface.
// No `preRbacValue`: admin capability; pre-RBAC orgs fall back to legacy
// `isAdmin`. The Databases feature is post-RBAC (LD flag + on-prem agent),
// matching the npm_registry carve-out.
export const MANAGE_APP_DATABASES = {
  resourceType: ResourceTypeEnum.APP_DATABASES,
  action: ActionTypeEnum.MANAGE,
};

// APPS-4517: gate for the per-org Package Registry page + write controls.
// Carved out of `org.manage` (Owner-only) onto the dedicated
// `npm_registry.manage` resource so org Admins — not just the Owner — can
// configure registries (the server authorizes POST/PUT/DELETE/verify on it).
// No `preRbacValue`: this is an admin capability, so pre-RBAC orgs fall back to
// the legacy `isAdmin` flag rather than granting it to every member.
export const MANAGE_NPM_REGISTRY = {
  resourceType: ResourceTypeEnum.NPM_REGISTRY,
  action: ActionTypeEnum.MANAGE,
};

// Gate for modifying the org-level default AI integration. Server authorizes
// PUT/DELETE /api/v1/organizations/:orgId/settings/app_ai on the dedicated
// `integrations.default_ai` resource so admins can grant "configure the AI
// default" without also handing over `org.manage`. Pre-RBAC orgs still rely on
// the legacy admin flag (`preRbacValue: true`) — they get the same access they
// had when the check was on ORGANIZATION.MANAGE.
export const MANAGE_AI_DEFAULT_INTEGRATION = {
  resourceType: ResourceTypeEnum.INTEGRATIONS_DEFAULT_AI,
  action: ActionTypeEnum.MANAGE,
  preRbacValue: true,
};

export const MANAGE_AI_INFERENCE = {
  resourceType: ResourceTypeEnum.AI_INFERENCE,
  action: ActionTypeEnum.MANAGE,
  preRbacValue: true,
};

export const READ_ORGANIZATION = {
  resourceType: ResourceTypeEnum.ORGANIZATION,
  action: ActionTypeEnum.READ,
  preRbacValue: true,
};

/* Users and groups permissions */
export const VIEW_USERS = {
  resourceType: ResourceTypeEnum.ORGANIZATION_USERS,
  action: ActionTypeEnum.READ,
  preRbacValue: true,
};

export const MANAGE_USERS = {
  resourceType: ResourceTypeEnum.ORGANIZATION_USERS,
  action: ActionTypeEnum.MANAGE,
};

export const VIEW_GROUPS = {
  resourceType: ResourceTypeEnum.GROUPS,
  action: ActionTypeEnum.READ,
  preRbacValue: true,
};

export const MANAGE_GROUPS = {
  resourceType: ResourceTypeEnum.GROUPS,
  action: ActionTypeEnum.MANAGE,
};

export const VIEW_GROUP_MEMBERS = {
  resourceType: ResourceTypeEnum.GROUPS_MEMBERS,
  action: ActionTypeEnum.READ,
  preRbacValue: true,
};

export const MANAGE_GROUP_MEMBERS = {
  resourceType: ResourceTypeEnum.GROUPS_MEMBERS,
  action: ActionTypeEnum.MANAGE,
  preRbacValue: true,
};

export const VIEW_ROLES = {
  resourceType: ResourceTypeEnum.ROLES,
  action: ActionTypeEnum.READ,
  preRbacValue: true,
};

export const READ_LOGS = {
  resourceType: ResourceTypeEnum.LOGS,
  action: ActionTypeEnum.READ,
  preRbacValue: true,
};

export const MANAGE_FOLDERS = {
  resourceType: ResourceTypeEnum.FOLDERS,
  action: ActionTypeEnum.MANAGE,
  preRbacValue: true,
};

export const MANAGE_ROLES = {
  resourceType: ResourceTypeEnum.ROLES,
  action: ActionTypeEnum.MANAGE,
};

/* Integrations and secret stores */
export const VIEW_SECRET_STORES = {
  resourceType: ResourceTypeEnum.SECRETS_STORES,
  action: ActionTypeEnum.CREATE,
};

export const CREATE_INTEGRATION = {
  resourceType: ResourceTypeEnum.INTEGRATIONS,
  action: ActionTypeEnum.CREATE,
};

export const CREATE_SECRET_STORE = {
  resourceType: ResourceTypeEnum.SECRETS_STORES,
  action: ActionTypeEnum.CREATE,
};

/* Entities */
export const CREATE_APPLICATIONS = {
  resourceType: ResourceTypeEnum.APPLICATIONS,
  action: ActionTypeEnum.CREATE,
  preRbacValue: true,
};

export const CREATE_WORKFLOWS = {
  resourceType: ResourceTypeEnum.WORKFLOWS,
  action: ActionTypeEnum.CREATE,
  preRbacValue: true,
};

export const CREATE_JOBS = {
  resourceType: ResourceTypeEnum.SCHEDULED_JOBS,
  action: ActionTypeEnum.CREATE,
  preRbacValue: true,
};

/* Organization Knowledge */
export const CREATE_ORG_KNOWLEDGE = {
  resourceType: ResourceTypeEnum.ORG_KNOWLEDGE,
  action: ActionTypeEnum.CREATE,
};

export const UPDATE_ORG_KNOWLEDGE = {
  resourceType: ResourceTypeEnum.ORG_KNOWLEDGE,
  action: ActionTypeEnum.UPDATE,
};

export const DELETE_ORG_KNOWLEDGE = {
  resourceType: ResourceTypeEnum.ORG_KNOWLEDGE,
  action: ActionTypeEnum.DELETE,
};
